/* eslint-disable camelcase */
import '@wojtekmaj/react-daterange-picker/dist/DateRangePicker.css';
import 'react-calendar/dist/Calendar.css';
import DateRangePicker from '@wojtekmaj/react-daterange-picker';

import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useState, useMemo, useEffect } from 'react';
import {
  Modal,
  Button,
  Stack,
  Stepper,
  Text,
  Box,
  Radio,
  ActionIcon,
  Loader,
  Divider,
  Alert,
  Badge,
  Progress,
  Group,
  Title,
  Transition
} from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import {
  IconPlus,
  IconCircleMinus,
  IconWorldSearch,
  IconPlaneDeparture,
  IconX,
  IconCircleCheck,
  IconTransferVertical,
  IconBell,
  IconCalendar
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import { enqueueSnackbar } from 'notistack';

import AirportAutocomplete from 'ui-components/autocompletes/AirportAutocomplete';
import AISearchResponseContent from 'ui-components/dialogs/AISearchResponseContent';
import MileageResultsDrawer from 'ui-components/dialogs/MileageResultsDrawer';
import callAzureFunction from 'utils/callAzureFunctions';
import { flightTypes, pointsScannerEnabled } from 'utils/constants';
import { setUserData } from 'store/features/userSlice';
import { coral } from 'theme/colors';
import { semanticColors } from 'theme/semanticColors';
import { radius } from 'theme/radius';
import { shadows } from 'theme/shadows';

// Map internal trip type values to the API-expected values
const tripTypeToApi = {
  [flightTypes.OneWayTrip]: 'one_way',
  [flightTypes.RoundTrip]: 'round_trip'
};

// ---------------------------------------------------------------------------
// Confirmation state shown after a successful async search submission
// ---------------------------------------------------------------------------
const SearchSubmittedConfirmation = ({ message, departureAirport, destinationAirport, dateRange, tripType, daysOfStay, onClose }) => {
  const [startDate, endDate] = dateRange;
  const tripLabel = tripType === flightTypes.RoundTrip ? 'Round Trip' : 'One Way';

  return (
    <Transition mounted transition="fade" duration={400}>
      {(styles) => (
        <Stack
          align="center"
          gap="lg"
          style={{ ...styles, paddingTop: 40, paddingBottom: 40, paddingLeft: 16, paddingRight: 16, textAlign: 'center' }}
        >
          {/* Success icon */}
          <Box
            style={{
              width: 72,
              height: 72,
              borderRadius: '50%',
              backgroundColor: semanticColors.status.success,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 6px 24px rgba(16, 185, 129, 0.3)',
              animation: 'confirmPulse 2s ease-in-out infinite'
            }}
          >
            <IconCircleCheck size={40} color="white" />
          </Box>

          <style>{`
            @keyframes confirmPulse {
              0%, 100% { box-shadow: 0 6px 24px rgba(16, 185, 129, 0.3); }
              50% { box-shadow: 0 6px 32px rgba(16, 185, 129, 0.5); }
            }
          `}</style>

          {/* Title */}
          <Box>
            <Title order={3} fw={700} c={semanticColors.text.primary} mb={4}>
              Search Submitted
            </Title>
            <Text size="sm" c={semanticColors.text.secondary} style={{ maxWidth: 380, margin: '0 auto', lineHeight: 1.7 }}>
              {message || "AI flight deal is being processed. You will be notified when it's ready."}
            </Text>
          </Box>

          {/* Request summary card */}
          <Box
            style={{
              width: '100%',
              maxWidth: 360,
              backgroundColor: semanticColors.surface.raised,
              borderRadius: 10,
              border: `1px solid ${semanticColors.border.default}`,
              padding: '20px 24px'
            }}
          >
            {/* Route */}
            <Group justify="center" align="center" gap={8} mb={12}>
              <Title order={4} fw={700} c={coral[6]}>
                {departureAirport?.iata}
              </Title>
              <IconTransferVertical size={20} color={semanticColors.text.secondary} />
              <Title order={4} fw={700} c={coral[6]}>
                {destinationAirport?.iata}
              </Title>
            </Group>

            <Divider mb={12} />

            {/* Details */}
            <Stack gap={8}>
              <Group gap={8} align="center">
                <IconCalendar size={16} color={semanticColors.text.secondary} />
                <Text size="sm" c={semanticColors.text.secondary}>
                  {dayjs(startDate).format('MMM DD')} - {dayjs(endDate).format('MMM DD, YYYY')}
                </Text>
              </Group>
              <Group gap={8} align="center">
                <IconPlaneDeparture size={16} color={semanticColors.text.secondary} />
                <Text size="sm" c={semanticColors.text.secondary}>
                  {tripLabel}
                  {tripType === flightTypes.RoundTrip && ` \u00B7 ${daysOfStay} day${daysOfStay > 1 ? 's' : ''} stay`}
                </Text>
              </Group>
            </Stack>
          </Box>

          {/* Notification hint */}
          <Group gap={8} align="center" px="sm">
            <IconBell size={18} color={coral[6]} />
            <Text size="xs" c={semanticColors.text.secondary} style={{ lineHeight: 1.5 }}>
              You will receive a notification when your results are ready.
            </Text>
          </Group>

          {/* Close button */}
          <Button variant="filled" size="lg" onClick={onClose} mt={8} radius={24} fw={600} px={40} style={{ fontSize: '0.95rem' }}>
            Got it
          </Button>
        </Stack>
      )}
    </Transition>
  );
};

SearchSubmittedConfirmation.propTypes = {
  message: PropTypes.string,
  departureAirport: PropTypes.object,
  destinationAirport: PropTypes.object,
  dateRange: PropTypes.array.isRequired,
  tripType: PropTypes.string.isRequired,
  daysOfStay: PropTypes.number.isRequired,
  onClose: PropTypes.func.isRequired
};

// ---------------------------------------------------------------------------
// Main dialog component
// ---------------------------------------------------------------------------
const AISearchCheapFlightDialog = ({ open, onClose, initialData = null }) => {
  const dispatch = useDispatch();
  const userData = useSelector((state) => state.user?.data);

  const { aiSearchDealLimit } = userData || {};

  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [responseData, setResponseData] = useState(null);
  const [aiSearchDealLimitState, setAiSearchDealLimitState] = useState(aiSearchDealLimit || 3);

  // Async submission state
  const [submissionResult, setSubmissionResult] = useState(null);

  // Set initial data when provided (e.g., when opening from a card click or notification)
  useEffect(() => {
    if (open && initialData) {
      setResponseData(initialData);
    }
  }, [open, initialData]);

  const [dateRange, setDateRange] = useState([new Date(), new Date()]);
  const [formattedStartDate, setFormattedStartDate] = useState();
  const [formattedEndDate, setFormattedEndDate] = useState();

  const [departureAirport, setDepartureAirport] = useState(null);
  const [destinationAirport, setDestinationAirport] = useState(null);

  const [tripType, setTripType] = useState(flightTypes.OneWayTrip);
  const [daysOfStay, setDaysOfStay] = useState(1);

  const [scanPointsData, setScanPointsData] = useState(null);

  const filteredResponseData = useMemo(() => {
    if (!responseData?.flight_options) return responseData;
    const cutoff = dayjs().add(3, 'day').startOf('day');
    const filtered = responseData.flight_options.filter(
      (opt) => opt.suggested_departure && !dayjs(opt.suggested_departure).isBefore(cutoff)
    );
    return { ...responseData, flight_options: filtered };
  }, [responseData]);

  const isLimitExhausted = aiSearchDealLimitState <= 0;

  const isMobile = useMediaQuery('(max-width: 575px)');

  // Determine which view mode we are in
  const isViewingExistingDeal = Boolean(responseData && initialData);
  const isConfirmationShown = Boolean(submissionResult);
  const isSearchMode = !responseData && !submissionResult;

  const getIsScanValid = () => {
    const [startDate, endDate] = dateRange;

    if (departureAirport?.iata === destinationAirport?.iata) {
      return {
        isValid: false,
        errMessage: 'Departure airport cannot be the same with the destination airport. Please change either one of the airport.'
      };
    }
    if (dayjs(startDate).isSame(endDate, 'date')) {
      return {
        isValid: false,
        errMessage: 'Start date cannot be the same with the end date. Please change either the one of the dates.'
      };
    }

    return { isValid: true };
  };

  const getCheapFlightDeals = async () => {
    const [startDate, endDate] = dateRange;

    try {
      setIsLoading(true);

      const { isValid, errMessage } = getIsScanValid();

      if (!isValid) {
        enqueueSnackbar({ variant: 'error', message: errMessage, anchorOrigin: { vertical: 'top', horizontal: 'right' } });
        return;
      }

      const data = {
        origin: departureAirport?.iata,
        destination: destinationAirport?.iata,
        date_from: dayjs(startDate).format('YYYY-MM-DD'),
        date_to: dayjs(endDate).format('YYYY-MM-DD'),
        trip_type: tripTypeToApi[tripType],
        currency: 'USD'
      };

      if (tripType === flightTypes.RoundTrip) {
        data.trip_duration_days = daysOfStay;
      }

      const response = await callAzureFunction({
        url: 'ai-search-flight-deals-flexible',
        method: 'post',
        data
      });

      const result = response.data;

      // Update search credits if the API returns the updated limit
      if (result.ai_search_deal_limit != null) {
        dispatch(setUserData({ data: { aiSearchDealLimit: result.ai_search_deal_limit } }));
        setAiSearchDealLimitState(result.ai_search_deal_limit);
      }

      // The API now returns immediately with { message, instance_id }
      setSubmissionResult({
        message: result.message,
        instanceId: result.instance_id
      });
    } catch (error) {
      enqueueSnackbar(
        <Stack gap={0}>
          <Title order={4} style={{ color: 'white', paddingLeft: 6 }}>
            No Cheap Flight Deal Found
          </Title>
          <Text size="sm" style={{ color: 'white', padding: 6, textAlign: 'justify' }}>
            Apologies, no cheap flight deal has been found. Please try again with another airport/ range of date.
          </Text>
        </Stack>,
        {
          variant: 'warning',
          anchorOrigin: { horizontal: 'right', vertical: 'top' }
        }
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeDate = (date) => {
    if (date) {
      setDateRange(date);
    } else {
      setDateRange([new Date(), new Date()]);
    }
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
    setResponseData(null);
    setSubmissionResult(null);
    setDateRange([new Date(), new Date()]);
    setDepartureAirport(null);
    setDestinationAirport(null);
    setTripType(flightTypes.OneWayTrip);
    setDaysOfStay(1);
    setScanPointsData(null);
  };

  useEffect(() => {
    const [startDate, endDate] = dateRange;

    if (startDate && endDate) {
      if (dayjs(endDate).diff(startDate, 'day') > 60) {
        enqueueSnackbar('Please select a date range within 60 days apart.', {
          variant: 'info',
          anchorOrigin: { horizontal: 'right', vertical: 'top' }
        });
        setDateRange([new Date(), new Date()]);
        return;
      }
    }
    if (!dayjs(startDate).isSame(endDate)) {
      setFormattedStartDate(dayjs(startDate).format('MMM DD, YYYY'));
      setFormattedEndDate(dayjs(endDate).format('MMM DD, YYYY'));
    } else {
      setFormattedStartDate(null);
      setFormattedEndDate(null);
    }
  }, [dateRange]);

  useEffect(() => {
    if (aiSearchDealLimit != null) {
      setAiSearchDealLimitState(aiSearchDealLimit);
    }
  }, [aiSearchDealLimit]);

  const steps = useMemo(
    () => [
      {
        label: 'Select your departure airport',
        description: 'Pick the departure airport',
        Component: (
          <AirportAutocomplete
            textFieldLabel="Departure"
            value={departureAirport}
            onChange={(_, value) => setDepartureAirport(value)}
            sx={{ maxWidth: '100%' }}
          />
        ),
        isNextStepDisabled: !departureAirport
      },
      {
        label: 'Select your destination airport',
        description: 'Pick your destination',
        Component: (
          <AirportAutocomplete
            textFieldLabel="Destination"
            value={destinationAirport}
            onChange={(_, value) => setDestinationAirport(value)}
            sx={{ maxWidth: '100%' }}
          />
        ),
        isNextStepDisabled: !destinationAirport
      },
      {
        label: 'Choose the type of flight',
        description: null,
        Component: (
          <Radio.Group value={tripType} onChange={(val) => setTripType(val)}>
            <Stack gap="xs" mt="xs">
              <Radio value={flightTypes.OneWayTrip} label="One Way Trip" />
              <Radio value={flightTypes.RoundTrip} label="Round Trip" />
            </Stack>
          </Radio.Group>
        ),
        isNextStepDisabled: !tripType
      },
      {
        label: 'Set the number of days of your stay',
        description: 'How many days will you be staying at your destination?',
        Component: (
          <Group gap="md" align="center">
            <ActionIcon
              variant="subtle"
              onClick={() => {
                if (daysOfStay <= 1) return;
                setDaysOfStay((prev) => prev - 1);
              }}
            >
              <IconCircleMinus size={24} color={coral[6]} />
            </ActionIcon>
            <Title order={4} px="sm">
              {daysOfStay} day{daysOfStay > 1 && 's'}
            </Title>
            <ActionIcon
              variant="subtle"
              onClick={() => {
                if (daysOfStay >= 30) return;
                setDaysOfStay((prev) => prev + 1);
              }}
            >
              <IconPlus size={24} color={coral[6]} />
            </ActionIcon>
          </Group>
        ),
        isNextStepDisabled: !tripType,
        skip: tripType === flightTypes.OneWayTrip
      },
      {
        label: 'Select a date range to scan',
        description: 'Pick dates to scan cheapest flight deals',
        Component: (
          <Box style={{ zIndex: 100, maxWidth: '100%', paddingBottom: 4 }}>
            <DateRangePicker
              value={dateRange}
              onChange={handleChangeDate}
              minDate={dayjs().toDate()}
              maxDate={dayjs().add(120, 'days').toDate()}
            />
          </Box>
        ),
        isNextStepDisabled: !(formattedStartDate && formattedEndDate) || isLoading || isLimitExhausted
      }
    ],
    [
      dateRange,
      departureAirport,
      destinationAirport,
      tripType,
      formattedStartDate,
      formattedEndDate,
      isLoading,
      daysOfStay,
      isLimitExhausted
    ]
  );

  const filteredSteps = steps.filter((step) => !step.skip);

  const getScanButtonIcon = (index) => {
    if (index !== filteredSteps.length - 1) return null;
    if (isLoading) return <Loader size={20} color="white" />;
    return <IconWorldSearch size={20} />;
  };

  const handleNext = () => {
    if (activeStep === filteredSteps.length - 1) {
      if (isLimitExhausted) return;
      getCheapFlightDeals();
    } else {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleClose = () => {
    handleReset();
    onClose();
  };

  // Determine whether to show the credits chip
  const showCreditsChip = isSearchMode;

  return (
    <Modal
      opened={open}
      onClose={handleClose}
      fullScreen={isMobile}
      size={scanPointsData ? 960 : 600}
      radius={radius.surface}
      shadow={shadows.modal}
      styles={{
        content: { transition: 'max-width 0.3s ease' },
        body: { padding: 0, display: 'flex', overflow: isViewingExistingDeal ? 'hidden' : 'visible' }
      }}
      withCloseButton={false}
      title={
        <Box style={{ width: '100%' }}>
          {isMobile ? (
            <>
              <Group align="center" mb={8}>
                <Group gap={12} align="center" style={{ flex: 1 }}>
                  <IconPlaneDeparture size={22} color={coral[6]} />
                  <Box>
                    <Title order={3} c={coral[6]} fw={600}>
                      AI Flight Search
                    </Title>
                    <Text size="xs" c={semanticColors.text.secondary}>
                      Find the cheapest deals for your trip
                    </Text>
                  </Box>
                </Group>
                {/* Mobile close button for confirmation / existing deal views */}
                {!isSearchMode && (
                  <ActionIcon size="sm" variant="subtle" onClick={handleClose}>
                    <IconX size={16} />
                  </ActionIcon>
                )}
              </Group>
              <Group justify="flex-end" mb={12}>
                {showCreditsChip ? (
                  <Badge
                    size="sm"
                    color={isLimitExhausted ? 'red' : undefined}
                    fw={600}
                    style={{ fontSize: '0.75rem', paddingLeft: 4, paddingRight: 4 }}
                  >
                    {aiSearchDealLimitState} search{aiSearchDealLimitState !== 1 ? 'es' : ''} left
                  </Badge>
                ) : (
                  <Box />
                )}
              </Group>
            </>
          ) : (
            <Group justify="space-between" align="center" mb={12}>
              <Group gap={12} align="center">
                <IconPlaneDeparture size={28} color={coral[6]} />
                <Box>
                  <Title order={3} c={coral[6]} fw={600}>
                    AI Flight Search
                  </Title>
                  <Text size="xs" c={semanticColors.text.secondary}>
                    Find the cheapest deals for your trip
                  </Text>
                </Box>
              </Group>
              {showCreditsChip && (
                <Badge
                  size="lg"
                  color={isLimitExhausted ? 'red' : undefined}
                  fw={600}
                  style={{ fontSize: '0.875rem', paddingLeft: 8, paddingRight: 8 }}
                >
                  {aiSearchDealLimitState} search{aiSearchDealLimitState !== 1 ? 'es' : ''} left
                </Badge>
              )}
            </Group>
          )}
          <Divider color={coral[2]} />
          <Transition mounted={isLoading} transition="fade" duration={200}>
            {(styles) => <Progress style={{ ...styles, marginTop: 4 }} size="xs" value={100} animated />}
          </Transition>
        </Box>
      }
    >
      {/* Left panel */}
      <Box
        style={{
          flex: 1,
          minWidth: 0,
          padding: isMobile ? 16 : 32,
          overflow: isViewingExistingDeal ? 'auto' : 'visible',
          position: 'relative',
          ...(isMobile && scanPointsData ? { display: 'none' } : {})
        }}
      >
        {/* Loading overlay (only during search submission) */}
        <Transition mounted={isLoading} transition="fade" duration={200}>
          {(styles) => (
            <Box
              style={{
                ...styles,
                position: 'absolute',
                inset: 0,
                backgroundColor: 'rgba(255,255,255,0.88)',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 16
              }}
            >
              <Loader size={40} />
              <Text fw={500} c={semanticColors.text.secondary}>
                Submitting your search...
              </Text>
            </Box>
          )}
        </Transition>

        {/* ---- VIEW MODE: Existing deal (opened with initialData) ---- */}
        {isViewingExistingDeal && filteredResponseData?.flight_options?.length > 0 && (
          <AISearchResponseContent data={filteredResponseData} onScanPoints={pointsScannerEnabled ? setScanPointsData : undefined} />
        )}

        {isViewingExistingDeal && filteredResponseData?.flight_options?.length === 0 && (
          <Box
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              paddingTop: 48,
              paddingBottom: 48,
              gap: 16
            }}
          >
            <Title order={4} c={semanticColors.text.secondary}>
              All deals have expired
            </Title>
            <Text size="sm" c={semanticColors.text.secondary} ta="center">
              All flight options for this search are departing within the next 3 days. Please search again with a later date range.
            </Text>
          </Box>
        )}

        {/* ---- CONFIRMATION MODE: Search was submitted successfully ---- */}
        {isConfirmationShown && (
          <SearchSubmittedConfirmation
            message={submissionResult.message}
            departureAirport={departureAirport}
            destinationAirport={destinationAirport}
            dateRange={dateRange}
            tripType={tripType}
            daysOfStay={daysOfStay}
            onClose={handleClose}
          />
        )}

        {/* ---- SEARCH MODE: Stepper form ---- */}
        {isSearchMode && (
          <>
            {isLimitExhausted && (
              <Alert color="yellow" mb="md">
                You have used all your AI search credits. Your credits will refresh next month. Upgrade your membership to get more search
                credits.
              </Alert>
            )}
            <Stepper active={activeStep} orientation="vertical" size="sm">
              {filteredSteps.map((step, index) => (
                <Stepper.Step
                  key={step.label}
                  label={<Title order={5}>{step.label}</Title>}
                  description={
                    step.description ? (
                      <Text size="sm" c={semanticColors.text.secondary} ta="left">
                        {step.description}
                      </Text>
                    ) : null
                  }
                >
                  <Box py="sm">{step.Component}</Box>
                  <Group gap="xs" mb="sm">
                    <Button
                      variant="filled"
                      onClick={handleNext}
                      mt={8}
                      disabled={step.isNextStepDisabled || isLoading}
                      leftSection={getScanButtonIcon(index)}
                    >
                      {index === filteredSteps.length - 1 ? 'Scan' : 'Continue'}
                    </Button>
                    <Button variant="subtle" disabled={index === 0} onClick={handleBack} mt={8}>
                      Back
                    </Button>
                  </Group>
                </Stepper.Step>
              ))}
            </Stepper>
          </>
        )}
      </Box>

      {/* Right panel -- Points Scanner (only for existing deal view) */}
      {scanPointsData && (
        <>
          {!isMobile && <Divider orientation="vertical" />}
          <Box
            style={{
              width: isMobile ? '100%' : 380,
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
              borderLeft: isMobile ? 'none' : undefined
            }}
          >
            <MileageResultsDrawer
              key={`${scanPointsData.origin}-${scanPointsData.destination}-${scanPointsData.date}`}
              origin={scanPointsData.origin}
              destination={scanPointsData.destination}
              date={scanPointsData.date}
              onClose={() => setScanPointsData(null)}
            />
          </Box>
        </>
      )}

      {/* Footer actions */}
      <Box
        style={{
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          padding: '8px 24px 16px',
          backgroundColor: semanticColors.surface.base,
          zIndex: 5
        }}
      >
        {(() => {
          if (isViewingExistingDeal) {
            return (
              <Group justify="flex-end" style={{ width: '100%' }}>
                <Button variant="subtle" onClick={handleClose}>
                  Close
                </Button>
              </Group>
            );
          }
          if (isConfirmationShown) {
            return (
              <Group justify="space-between" style={{ width: '100%' }}>
                <Button variant="subtle" onClick={handleReset} size="xs">
                  Search Again
                </Button>
                <Button variant="subtle" onClick={handleClose}>
                  Close
                </Button>
              </Group>
            );
          }
          return (
            <Group justify="flex-end" gap="xs">
              <Button variant="subtle" onClick={handleReset}>
                Reset
              </Button>
              <Button variant="subtle" onClick={handleClose}>
                Cancel
              </Button>
            </Group>
          );
        })()}
      </Box>
    </Modal>
  );
};

AISearchCheapFlightDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  initialData: PropTypes.object
};

export default AISearchCheapFlightDialog;
